import { motion, AnimatePresence } from 'framer-motion';
import { useContext, useMemo } from 'react';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import { useKpiCollection } from '../hooks/useKpiCollection';
import classNames from 'classnames';
import ListeningView from './ListeningView';
import { SentimentKpiPoint } from '@configTypes/kpiSentiment';

type Sentiment = {
  date: number;
  keyword: string;
  sentiment: 'positive' | 'negative';
  speaker: string;
};

export function SentimentView() {
  const eventConfig = useContext(KlartextEventConfigContext);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sentimentKpi, loadingPositives, errorPositives] = useKpiCollection<SentimentKpiPoint[]>({
    eventId: eventConfig?.eventId ?? '',
    kpiName: 'Sentiment',
    initialState: [],
  });

  const sentiments = useMemo(() => {
    const allSentiments: Sentiment[] = [];
    sentimentKpi
      .filter(
        item =>
          item.value.speaker === eventConfig?.frontendSpecificConfig?.participants.democrat.name ||
          item.value.speaker === eventConfig?.frontendSpecificConfig?.participants.republican.name,
      )
      .forEach(item => {
        const { statements } = item.value;

        statements.forEach(statement => {
          statement.positive?.forEach(keyword => {
            allSentiments.push({ date: item.date, keyword, sentiment: 'positive', speaker: item.value.speaker });
          });
          statement.negative?.forEach(keyword => {
            allSentiments.push({ date: item.date, keyword, sentiment: 'negative', speaker: item.value.speaker });
          });
        });
      });
    // Filter out objects with the same keyword and sort the combined array by date
    return allSentiments
      .filter((value, index, self) => self.findIndex(t => t.keyword === value.keyword) === index)
      .sort((a, b) => b.date - a.date);
  }, [sentimentKpi]);

  if (sentimentKpi.length === 0) {
    return <ListeningView />;
  }

  return (
    <div className="flex flex-1 flex-col items-stretch gap-1">
      {/* Column headers */}
      <div className="bg-gray-100 mb-2 flex justify-between rounded-lg px-4 py-2">
        <h2 className="text-xl font-bold text-black">
          {eventConfig?.frontendSpecificConfig?.participants.democrat.name}
        </h2>
        <h2 className="text-xl font-bold text-black">
          {eventConfig?.frontendSpecificConfig?.participants.republican.name}
        </h2>
      </div>

      <div className="flex flex-1 flex-col items-stretch gap-1">
        <AnimatePresence>
          {sentiments.map((sentiment, i) => (
            <motion.div
              layout
              key={i}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
              className={classNames(
                'relative flex',
                sentiment.speaker === eventConfig?.frontendSpecificConfig?.participants.republican.name
                  ? 'justify-end'
                  : 'justify-start',
              )}>
              <span
                className={classNames(
                  `px-3.5 py-1.5 text-2xl font-bold`,
                  sentiment.speaker === eventConfig?.frontendSpecificConfig?.participants.republican.name
                    ? 'rounded-l-full rounded-r-none border-r-2 border-republican bg-eidraWhite'
                    : 'rounded-l-none rounded-r-full border-l-2 border-democrat bg-eidraWhite',
                  sentiment.sentiment === 'positive' ? 'text-eidraGreen' : 'text-eidraRed',
                )}>
                {sentiment.keyword}
              </span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
